import React from "react";
import {
    ArrowUpRight,
    PenTool,
    FileText,
    Cog,
    Linkedin,
    Sun,
    Server,
    Compass,
    Microscope,
    ClipboardList,
    Wrench,
    Building2,
    Thermometer,
    Droplets,
    Recycle,
} from "lucide-react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { GlassCard } from "../components/GlassCard";
import { Reveal } from "../lib/motion";

const SOLAR_IMG =
    "https://images.unsplash.com/photo-1508790762848-8a3096277c8f?crop=entropy&cs=srgb&fm=jpg&w=1400&q=80";

const DATACENTER_IMG =
    "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1400&q=80";

const services = [
    {
        id: "design",
        icon: PenTool,
        title: "Design",
        body: "Process and system design for desalination plants, from concept to validated configuration.",
    },
    {
        id: "dpr",
        icon: FileText,
        title: "DPR",
        body: "Detailed Project Reports: techno-economic feasibility, sizing, costing, and risk assessment.",
    },
    {
        id: "engineering",
        icon: Cog,
        title: "Engineering",
        body: "Engineering advisory and implementation support, bridging design to commissioned plant.",
    },
];

const applicationAreas = [
    "Data centers",
    "Power plants",
    "Process industries",
    "And more",
];

const capabilities = [
    {
        id: "data-center",
        img: DATACENTER_IMG,
        eyebrow: "Waste-heat recovery",
        icon: Server,
        title: "Data-Center Waste-Heat → Freshwater",
        body: "Capture low-grade waste heat from data centers to drive seawater desalination or polish river / groundwater into ultrapure water: the same pattern proven with power-plant waste-heat recovery, now applied to the fastest-growing thermal load on the grid.",
        tags: [
            "Heat recovery",
            "Seawater desalination",
            "Ultrapure water",
            "Data centers",
        ],
    },
    {
        id: "solar-thermal",
        img: SOLAR_IMG,
        eyebrow: "Renewable thermal",
        icon: Sun,
        title: "Concentrated Solar Thermal",
        body: "Multi-Effect Distillation: concentrated solar thermal energy for simultaneous electricity generation, desalination / water purification or green steam generation. A multi-use thermal cascade for sun-rich, water-stressed regions.",
        tags: ["CSP", "MED", "Desalination", "Water purification"],
    },
];

const engagement = [
    {
        icon: Compass,
        n: "01",
        title: "Discovery",
        body: "Site, source water, energy availability, regulatory context.",
    },
    {
        icon: Microscope,
        n: "02",
        title: "Feasibility",
        body: "Technology selection, process design, performance modelling.",
    },
    {
        icon: ClipboardList,
        n: "03",
        title: "DPR",
        body: "Detailed Project Report: capex/opex, BOQ, risk register.",
    },
    {
        icon: Wrench,
        n: "04",
        title: "Engineering",
        body: "Detailed engineering, vendor selection, commissioning support.",
    },
];

const Hero = () => (
    <section
        data-testid="consulting-hero"
        className="relative w-full min-h-[90vh] overflow-hidden flex items-center bg-ocean-deep grain pt-24 pb-16"
    >
        <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
        >
            <div
                className="absolute -top-[20%] -right-[10%] w-[60vw] h-[60vw] rounded-full opacity-50"
                style={{
                    background:
                        "radial-gradient(circle, #138A72 0%, rgba(19,138,114,0) 60%)",
                    filter: "blur(100px)",
                }}
            />
            <div
                className="absolute bottom-[10%] left-[5%] w-[40vw] h-[40vw] rounded-full opacity-30"
                style={{
                    background:
                        "radial-gradient(circle, #A3E635 0%, rgba(163,230,53,0) 60%)",
                    filter: "blur(90px)",
                }}
            />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full">
            <div className="max-w-4xl">
                <span className="section-label text-energy">Consulting</span>
                <h1
                    data-testid="consulting-hero-heading"
                    className="mt-6 font-display font-black text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter"
                >
                    Desalination expertise,{" "}
                    <span className="text-energy italic">on demand.</span>
                </h1>
                <p className="mt-8 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">
                    Three decades of plant design experience, applied to your
                    project. Design, Detailed Project Reports, and engineering
                    advisory across thermal, membrane, and hybrid desalination
                    systems.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                    <a
                        href="mailto:info@oceantooasis.in?subject=Consulting%20enquiry"
                        data-testid="consulting-hero-cta"
                        className="group inline-flex items-center gap-3 px-7 py-4 rounded-full bg-energy text-ocean font-bold text-sm tracking-wide hover:bg-white transition-all duration-300 shadow-[0_10px_40px_rgba(163,230,53,0.3)] hover:-translate-y-0.5"
                    >
                        Start a project
                        <ArrowUpRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                    </a>
                </div>
            </div>
        </div>
    </section>
);

const Services = () => (
    <section
        id="services"
        data-testid="consulting-services"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-lavender"
    >
        <div className="max-w-7xl mx-auto">
            <Reveal className="max-w-3xl">
                <span className="section-label">What we offer</span>
                <h2 className="mt-4 font-display font-black text-ocean text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                    Design. DPR. Engineering.
                </h2>
                <p className="mt-5 text-inkblue text-base md:text-lg leading-relaxed">
                    A focused consulting practice for organisations building
                    desalination capacity, from greenfield design through
                    bankable reports to delivered plant.
                </p>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {services.map((s, i) => (
                    <GlassCard
                        key={s.id}
                        className="p-8"
                        data-testid={`service-card-${i}`}
                    >
                        <div className="w-12 h-12 rounded-xl bg-teal/15 text-teal flex items-center justify-center">
                            <s.icon size={22} />
                        </div>
                        <h3 className="mt-6 font-display font-bold text-ocean text-2xl tracking-tight">
                            {s.title}
                        </h3>
                        <p className="mt-3 text-inkblue text-sm leading-relaxed">
                            {s.body}
                        </p>
                    </GlassCard>
                ))}
                <GlassCard
                    className="p-8 bg-ocean/[0.04]"
                    data-testid="service-card-application-areas"
                >
                    <div className="w-12 h-12 rounded-xl bg-energy/25 text-ocean flex items-center justify-center">
                        <Building2 size={22} />
                    </div>
                    <h3 className="mt-6 font-display font-bold text-ocean text-2xl tracking-tight">
                        Application areas
                    </h3>
                    <ul className="mt-4 flex flex-wrap gap-2">
                        {applicationAreas.map((a) => (
                            <li
                                key={a}
                                className="px-3 py-1.5 rounded-full bg-white/70 text-ocean text-xs font-semibold tracking-wide"
                            >
                                {a}
                            </li>
                        ))}
                    </ul>
                </GlassCard>
            </div>
        </div>
    </section>
);

const approach = [
    {
        id: "waste-heat-in",
        icon: Recycle,
        title: "Waste heat in",
        body: "Medium-grade heat that industry and power plants would otherwise reject to cooling towers or the sea.",
    },
    {
        id: "temp-range",
        icon: Thermometer,
        title: "50–150°C",
        body: "The low- to medium-temperature range we work with: too cool for power, ideal for desalination.",
    },
    {
        id: "med-lttd",
        icon: Droplets,
        title: "MED and LTTD",
        body: "Proven Multi-Effect Distillation and Low-Temperature Thermal Desalination.",
    },
    {
        id: "scalable",
        icon: Sun,
        title: "Scalable sizing",
        body: "Engineered to scale from pilot-scale output to full industrial and community needs.",
    },
];

const Approach = () => (
    <section
        data-testid="consulting-approach"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-mist"
    >
        <div className="max-w-7xl mx-auto">
            <Reveal className="max-w-3xl">
                <span className="section-label text-energy">The approach</span>
                <h2 className="mt-4 font-display font-black text-ocean text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                    Waste heat in, fresh water out.
                </h2>
                <p className="mt-5 text-inkblue text-base md:text-lg leading-relaxed">
                    Coastal industry and power plants reject enormous amounts of
                    medium-temperature heat. We capture it to drive proven
                    thermal desalination, turning a cooling load into a reliable
                    freshwater supply, with lower energy use and carbon than
                    conventional plants.
                </p>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {approach.map((a, i) => (
                    <Reveal key={a.id} delay={0.1 + i * 0.08} y={30}>
                        <GlassCard
                            className="p-7 h-full"
                            data-testid={`approach-card-${i}`}
                        >
                            <div className="w-11 h-11 rounded-xl bg-teal/15 text-teal flex items-center justify-center">
                                <a.icon size={20} />
                            </div>
                            <h3 className="mt-5 font-display font-bold text-ocean text-lg tracking-tight">
                                {a.title}
                            </h3>
                            <p className="mt-2 text-inkblue text-sm leading-relaxed">
                                {a.body}
                            </p>
                        </GlassCard>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);

const LeadExpert = () => (
    <section
        data-testid="consulting-lead"
        className="relative overflow-hidden py-24 md:py-32 px-6 md:px-10 bg-ocean-deep grain"
    >
        <div
            className="absolute top-1/2 right-0 -translate-y-1/2 w-[55vw] h-[55vw] rounded-full opacity-25 pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle, #138A72 0%, rgba(19,138,114,0) 60%)",
                filter: "blur(100px)",
            }}
        />
        <div className="relative max-w-7xl mx-auto">
            <span className="section-label text-energy">
                Led by Dr. Purnima Jalihal
            </span>
            <h2 className="mt-6 font-display font-black text-white text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-4xl">
                Three decades. Eight patents. One mission: desalination
                that works.
            </h2>
            <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-5 text-left text-white/80 text-base md:text-lg max-w-3xl">
                {[
                    "Decades designing desalination plants across thermal and membrane systems",
                    "8 patents in ocean technology",
                    "Former Head, Energy & Fresh Water Practice, National Institute of Ocean Technology (NIOT)",
                    "CTO, OceanToOasis",
                ].map((b) => (
                    <li key={b} className="flex items-start gap-4">
                        <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-energy shrink-0" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <a
                href="https://www.linkedin.com/in/purnima-jalihal-352511186/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-12 inline-flex items-center gap-2 px-5 py-3 rounded-full glass-dark text-white text-sm font-semibold hover:bg-white/10 transition"
                data-testid="consulting-lead-linkedin"
            >
                <Linkedin size={16} /> View profile
            </a>
        </div>
    </section>
);

const Capabilities = () => (
    <section
        data-testid="consulting-capabilities"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-lavender"
    >
        <div className="max-w-7xl mx-auto">
            <Reveal className="max-w-3xl">
                <span className="section-label">Example capabilities</span>
                <h2 className="mt-4 font-display font-black text-ocean text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
                    Where energy meets water.
                </h2>
                <p className="mt-5 text-inkblue text-base md:text-lg leading-relaxed">
                    Two project archetypes that capture how we think: pairing
                    underused thermal energy with high-value freshwater
                    production.
                </p>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
                {capabilities.map((c, i) => (
                    <GlassCard
                        key={c.id}
                        className="p-0 overflow-hidden"
                        data-testid={`capability-card-${i}`}
                    >
                        <div className="relative aspect-[16/10] overflow-hidden">
                            <img
                                src={c.img}
                                alt={c.title}
                                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                                loading="lazy"
                            />
                            <div
                                className="absolute inset-0 pointer-events-none"
                                style={{
                                    background:
                                        "linear-gradient(to top, rgba(15,42,68,0.5) 0%, rgba(15,42,68,0) 50%)",
                                }}
                            />
                            <div className="absolute top-5 left-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/85 backdrop-blur text-ocean text-[10px] font-bold uppercase tracking-[0.18em]">
                                <c.icon size={12} />
                                {c.eyebrow}
                            </div>
                        </div>
                        <div className="p-7 md:p-8">
                            <h3 className="font-display font-black text-ocean text-2xl md:text-3xl tracking-tight leading-tight">
                                {c.title}
                            </h3>
                            <p className="mt-4 text-inkblue text-sm md:text-base leading-relaxed">
                                {c.body}
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {c.tags.map((t) => (
                                    <span
                                        key={t}
                                        className="px-3 py-1.5 rounded-full bg-ocean/5 text-ocean/80 text-xs font-semibold"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </GlassCard>
                ))}
            </div>
        </div>
    </section>
);

const Engagement = () => (
    <section
        data-testid="consulting-engagement"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-lavender"
    >
        <div className="max-w-7xl mx-auto">
            <Reveal className="max-w-3xl">
                <span className="section-label">How we engage</span>
                <h2 className="mt-4 font-display font-black text-ocean text-4xl md:text-5xl tracking-tighter leading-[1.05]">
                    From brief to commissioned plant.
                </h2>
            </Reveal>
            <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {engagement.map((s, i) => (
                    <GlassCard
                        key={s.n}
                        className="p-7"
                        data-testid={`engagement-step-${i}`}
                    >
                        <div className="flex items-start justify-between">
                            <div className="w-12 h-12 rounded-xl bg-teal/15 text-teal flex items-center justify-center">
                                <s.icon size={22} />
                            </div>
                            <span className="font-display font-black text-ocean/10 text-5xl leading-none">
                                {s.n}
                            </span>
                        </div>
                        <h3 className="mt-6 font-display font-bold text-ocean text-xl tracking-tight">
                            {s.title}
                        </h3>
                        <p className="mt-3 text-inkblue text-sm leading-relaxed">
                            {s.body}
                        </p>
                    </GlassCard>
                ))}
            </div>
        </div>
    </section>
);

const CTA = () => (
    <section
        data-testid="consulting-cta"
        className="relative overflow-hidden py-24 md:py-32 px-6 md:px-10 bg-ocean-deep grain"
    >
        <div
            className="absolute -bottom-1/3 left-1/2 -translate-x-1/2 w-[70vw] h-[70vw] rounded-full opacity-30 pointer-events-none"
            style={{
                background:
                    "radial-gradient(circle, #A3E635 0%, rgba(163,230,53,0) 60%)",
                filter: "blur(110px)",
            }}
        />
        <div className="relative max-w-7xl mx-auto">
            <span className="section-label text-energy">Let's design it</span>
            <h2 className="mt-6 font-display font-black text-white text-4xl md:text-6xl tracking-tighter leading-[1.05] max-w-4xl">
                Have a water + energy challenge? Let's design the solution.
            </h2>
            <a
                href="mailto:info@oceantooasis.in?subject=Consulting%20enquiry"
                data-testid="consulting-bottom-cta"
                className="mt-12 group inline-flex items-center gap-3 px-8 py-5 rounded-full bg-energy text-ocean font-bold text-base hover:bg-white transition-all duration-300 shadow-[0_10px_40px_rgba(163,230,53,0.3)] hover:-translate-y-1"
            >
                Email the team
                <ArrowUpRight
                    size={18}
                    className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
            </a>
        </div>
    </section>
);

export default function Consulting() {
    return (
        <div className="bg-lavender min-h-screen">
            <Nav />
            <main>
                <Hero />
                <Services />
                <Approach />
                <LeadExpert />
                <Capabilities />
                <Engagement />
                <CTA />
            </main>
            <Footer />
        </div>
    );
}
